import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { App } from './App';
import { ApiError } from './lib/api';
import { SessionProvider } from './lib/session';
import './styles/index.css';

/**
 * A 401 from anywhere means the session is already gone server-side — an
 * administrator reset it, it was changed on another device, or it expired. Drop
 * the cached identity at once so a revoked tab cannot keep presenting a
 * signed-in shell (and offering actions that would all bounce) until something
 * happens to refetch.
 */
function dropStaleSession(error: unknown): void {
  if (!(error instanceof ApiError) || !error.isUnauthenticated) return;
  queryClient.setQueryData(['session'], null);
  queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== 'session' });
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: dropStaleSession }),
  mutationCache: new MutationCache({ onError: dropStaleSession }),
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 10_000,
    },
  },
});

const container = document.getElementById('root');
if (!container) {
  throw new Error('Root element #root is missing from index.html');
}

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <SessionProvider>
          <App />
        </SessionProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);

// Register the app-shell service worker in production builds only. In dev, Vite
// serves modules unbundled and a worker caching them would fight HMR, so this
// is gated on the production flag rather than registered everywhere.
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js', { updateViaCache: 'none' }).catch((error) => {
      // Registration is a progressive enhancement; a failure must not break the app.
      console.error('Service worker registration failed', error);
    });
  });
}
